import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuCalendarX, LuInfo, LuLoaderCircle, LuSave, LuTriangleAlert } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { useTranslation } from '../../i18n/LanguageContext';
import { deactivateEmployeeForExit } from '../../utils/employeeExit';
import { Field, TextInput } from './employeeForm/FormControls';

/**
 * İşten ayrılma tarihini sorar ve çıkışı tamamlar.
 *
 * @param reason      Pencerenin neden açıldığını anlatan bilgi notu (ör. çıkış
 *                    sözleşmesi yüklendiğinde). Verilmezse not gösterilmez.
 * @param defaultDate Tarih alanının başlangıç değeri ('yyyy-mm-dd').
 */
const ExitDateModal = ({
  selectedEmployee,
  onClose,
  onUpdate,
  onEmployeeUpdate,
  reason,
  defaultDate = '',
}) => {
  const { t, language } = useTranslation();
  const formId = useId();
  const [exitDate, setExitDate] = useState(defaultDate);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fullName = `${selectedEmployee?.eName || ''} ${selectedEmployee?.eSurname || ''}`.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!exitDate) {
      toast.error(t('employees.modals.exit.required'));
      setError(t('employees.modals.exit.required'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Projelerden pasife alma + Active: 0 + çıkış tarihi (bkz. utils/employeeExit)
      await deactivateEmployeeForExit(selectedEmployee.id, exitDate);

      // Dinamik olarak güncellenen çalışan bilgisini geri gönder
      const updatedEmployee = {
        ...selectedEmployee,
        Active: 0,
        employeeExitDate: exitDate
      };

      // Eğer onEmployeeUpdate fonksiyonu varsa, güncellenmiş çalışan bilgisini gönder
      if (onEmployeeUpdate) {
        onEmployeeUpdate(updatedEmployee);
      }

      toast.success(t('employees.modals.exit.saved'));
      await onUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating employee status:', err);
      toast.error(t('employees.modals.exit.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isSubmitting) onClose();
      }}
      size="md"
      title={t('employees.modals.exit.title')}
      subtitle={fullName || undefined}
      icon={(
        <GlassIcon gradient="tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30" size="xs">
          <LuCalendarX aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      closeLabel={t('common.close')}
      lang={language}
      footer={(
        <>
          <Button variant="ghost" onClick={() => onClose()} disabled={isSubmitting}>{t('employees.form.cancel')}</Button>
          <Button variant="primary" type="submit" form={formId} disabled={isSubmitting}>
            {isSubmitting
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {isSubmitting ? t('employees.form.saving') : t('employees.form.save')}
          </Button>
        </>
      )}
    >
      <form id={formId} noValidate onSubmit={handleSubmit} className="tw-space-y-4">
        {reason && (
          <p className="tw-flex tw-items-start tw-gap-2.5 tw-rounded-xl tw-bg-sky-500/10 tw-px-3.5 tw-py-3 tw-text-sm tw-leading-snug tw-text-sky-800 dark:tw-text-sky-200">
            <LuInfo aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
            <span className="tw-min-w-0">{reason}</span>
          </p>
        )}
        <Field
          label={t('employees.modals.exit.label', { name: fullName })}
          htmlFor={`${formId}-date`}
          required
          error={error}
        >
          <TextInput
            id={`${formId}-date`}
            type="date"
            value={exitDate}
            onChange={(e) => {
              setExitDate(e.target.value);
              setError('');
            }}
            error={error}
          />
        </Field>
        <p className="tw-flex tw-items-start tw-gap-2.5 tw-rounded-xl tw-bg-amber-400/10 tw-px-3.5 tw-py-3 tw-text-sm tw-leading-snug tw-text-amber-800 dark:tw-text-amber-200">
          <LuTriangleAlert aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
          <span className="tw-min-w-0">{t('employees.modals.exit.effect')}</span>
        </p>
      </form>
    </Modal>
  );
};

ExitDateModal.propTypes = {
  selectedEmployee: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onEmployeeUpdate: PropTypes.func,
  reason: PropTypes.string,
  defaultDate: PropTypes.string,
};

export default ExitDateModal;
